/**
 * InfinitSales - useChannels
 *
 * Carrega o mapa de capacidades por canal (GET /api/system/channels) uma vez
 * por sessao e o entrega a utils/channels.js, que e quem todo mundo consulta
 * (labelOf, supports, routeOf...). Mesmo padrao do useFeatureFlags: cache de
 * modulo, dedup de requisicoes em voo.
 *
 * Com CHANNEL_CAPABILITIES_API_ENABLED desligada o backend responde 404 e o
 * util fica na copia local dos 4 canais: comportamento identico ao de antes
 * do mapa existir. Nada aqui "pisca": a copia local ja responde de forma
 * sincrona desde o boot, o fetch so a substitui pelo que o backend diz.
 */
import { useEffect, useState } from 'react';
import api from '../services/global/api';
import {
  channelOrder, channelsFromBackend, getChannels, labelOf, resetChannelsSnapshot,
  setChannelsSnapshot, supports,
} from '../utils/channels';

let _loaded = false;     // ja tentou carregar nesta sessao (sucesso ou 404)
let _inflight = null;
const _listeners = new Set();

function notify() {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

async function loadChannels() {
  if (_loaded) return getChannels();
  if (!_inflight) {
    _inflight = api
      .get('/api/system/channels')
      .then((res) => {
        const data = res && res.data && res.data.data;
        setChannelsSnapshot(data);
      })
      .catch(() => { /* 404 (flag OFF) ou rede: fica na copia local */ })
      .finally(() => {
        _loaded = true;
        _inflight = null;
        notify();
      });
  }
  await _inflight;
  return getChannels();
}

/** Dispara o fetch cedo (boot), sem esperar um consumidor montar. */
export function preloadChannels() {
  loadChannels();
}

/** Volta ao estado inicial (logout e testes). */
export function resetChannels() {
  _loaded = false;
  _inflight = null;
  resetChannelsSnapshot();
  notify();
}

export default function useChannels() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(!_loaded);

  useEffect(() => {
    let alive = true;
    const rerender = () => { if (alive) force((n) => n + 1); };
    _listeners.add(rerender);
    if (!_loaded) {
      loadChannels().then(() => { if (alive) setLoading(false); });
    }
    return () => {
      alive = false;
      _listeners.delete(rerender);
    };
  }, []);

  return {
    channels: getChannels(),
    order: channelOrder(),
    loading,
    fromBackend: channelsFromBackend(),
    labelOf,
    supports,
  };
}
